import React, {useEffect, useState} from 'react';
import {useLocation} from 'react-router-dom';
import {useAuthStore} from '../../store/authStore';
import {getComments, postComment, deleteComment} from '../../api/comments';

function CommentBox() {
    const {pathname} = useLocation();
    const user = useAuthStore((state) => state.user);

    const [comments, setComments] = useState([]);
    const [showComments, setShowComments] = useState(false);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);

    useEffect(() => {
        let cancelled = false;
        setLoading(true);
        getComments(pathname)
            .then((data) => {
                if (!cancelled) setComments(data);
            })
            .catch((err) => {
                if (!cancelled) setError(err.message);
            })
            .finally(() => {
                if (!cancelled) setLoading(false);
            });
        return () => {
            cancelled = true;
        };
    }, [pathname]);

    const handleAddComment = async (body, authorName) => {
        const comment = await postComment({pageKey: pathname, body, authorName});
        setComments((prev) => prev.concat([comment]));
    };

    const handleDeleteComment = async (id) => {
        await deleteComment(id);
        setComments((prev) => prev.filter((c) => c.id !== id));
    };

    const buttonText = showComments ? 'Hide Comments' : 'Show Comments';

    return (
        <div className="w-100 mt-10 bg-gradient-to-br from-gray-50 via-orange-50 to-orange-50 dark:from-gray-950 dark:to-gray-900 md:py-16 border-green-100 z-10 text-blue-600 rounded-lg overlay">
            <h2>Add a comment</h2>
            <CommentForm user={user} addComment={handleAddComment}/>
            {error && <div className="text-red-600">{error}</div>}
            <button id="comment-reveal" onClick={() => setShowComments(!showComments)} className="mr-3">
                {buttonText}
            </button>
            <h3>Comments</h3>
            <h4 className="comment-count">
                {loading ? 'Loading comments...' : getCommentsTitle(comments.length)}
            </h4>
            {showComments &&
                <div className="comment-list">
                    {comments.map((comment) => (
                        <Comment
                            key={comment.id}
                            author={comment.author_name}
                            body={comment.body}
                            canDelete={Boolean(user) && comment.user_id === user.id}
                            onDelete={() => handleDeleteComment(comment.id)}
                        />
                    ))}
                </div>
            }
        </div>
    );
}

function getCommentsTitle(commentCount) {
    if (commentCount === 0) {
        return 'No comments yet';
    } else if (commentCount === 1) {
        return '1 comment';
    }
    return `${commentCount} comments`;
}

function CommentForm({user, addComment}) {
    const [body, setBody] = useState('');
    const [authorName, setAuthorName] = useState('');
    const [submitting, setSubmitting] = useState(false);
    const [error, setError] = useState(null);

    const handleSubmit = async (event) => {
        event.preventDefault();
        setError(null);
        setSubmitting(true);
        try {
            await addComment(body, user ? undefined : authorName);
            setBody('');
            setAuthorName('');
        } catch (err) {
            setError(err.message);
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <form className="comment-form overlay" onSubmit={handleSubmit}>
            <div className="comment-form-fields">
                {user
                    ? <p className="text-sm text-gray-500">Posting as {user.displayName}</p>
                    : <input placeholder="Name" required minLength={2} maxLength={100} value={authorName}
                             onChange={(e) => setAuthorName(e.target.value)}/>
                }
                <br/>
                <textarea placeholder="Comment" rows="4" required maxLength={2000} value={body}
                          onChange={(e) => setBody(e.target.value)}/>
            </div>
            {error && <div className="text-red-600">{error}</div>}
            <div className="comment-form-actions">
                <button type="submit" disabled={submitting}>
                    {submitting ? 'Posting...' : 'Post Comment'}
                </button>
            </div>
        </form>
    );
}

function Comment({author, body, canDelete, onDelete}) {
    return (
        <div className="comment">
            <p className="comment-header">{author}</p>
            <p className="comment-body">- {body}</p>
            <div className="comment-footer">
                {canDelete &&
                    <a href="#" className="comment-footer-delete" onClick={(e) => {
                        e.preventDefault();
                        onDelete();
                    }}>Delete Comment</a>
                }
            </div>
        </div>
    );
}

export default CommentBox;
